/* ==========================================================================
   Pricing list modals (PORTAL-204)
   New (blank / duplicate / percent off), edit name + description, and delete
   with "move providers to". Each modal owns its form state and calls the API;
   the page refetches through onDone.
   ========================================================================== */

import { useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { FormField } from '@/components/ui/FormField'
import { useToast } from '@/context/ToastContext'
import { createPricingList, deletePricingList, updatePricingList } from '@/api/admin'
import { ApiClientError } from '@/api/client'
import type { PricingList, PricingListStartFrom } from '@/api/types'
import { providerCountLabel } from '@/utils/pricing'

/** Select value standing in for "no list" (the default price). */
export const DEFAULT_PRICE_OPTION = 'default'
export const DEFAULT_PRICE_LABEL = 'Default price (no list)'

const NAME_REQUIRED = 'Give the list a name providers will recognize.'

function nameTaken(lists: PricingList[], name: string, excludeId?: string): boolean {
  const lower = name.trim().toLowerCase()
  return lists.some((l) => l.id !== excludeId && l.name.toLowerCase() === lower)
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiClientError ? err.message : fallback
}

/* --------------------------------------------------------------------------
   New pricing list (also "Duplicate", preset with a source list)
   -------------------------------------------------------------------------- */

export interface NewPricingListPreset {
  name: string
  startFrom: PricingListStartFrom
  sourceListId: string | null
}

interface NewPricingListModalProps {
  open: boolean
  preset: NewPricingListPreset | null
  slug: string
  lists: PricingList[]
  productTotal: number
  onClose: () => void
  onDone: () => void
}

const START_FROM_OPTIONS: { key: PricingListStartFrom; label: string; hint: string }[] = [
  { key: 'blank', label: 'Empty list', hint: 'Everything inherits the default price' },
  { key: 'duplicate', label: 'Duplicate an existing list', hint: 'Copies every entry from the list you pick' },
  { key: 'percent_off', label: 'Percent off default', hint: 'One rule applied to every product' },
]

export function NewPricingListModal({
  open,
  preset,
  slug,
  lists,
  productTotal,
  onClose,
  onDone,
}: NewPricingListModalProps) {
  const { success: showSuccess, error: showError } = useToast()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [startFrom, setStartFrom] = useState<PricingListStartFrom>('blank')
  const [sourceListId, setSourceListId] = useState<string | null>(null)
  const [percentOff, setPercentOff] = useState('10')
  const [nameError, setNameError] = useState('')
  const [seedError, setSeedError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(preset?.name ?? '')
    setDescription('')
    setStartFrom(preset?.startFrom ?? 'blank')
    setSourceListId(preset?.sourceListId ?? lists[0]?.id ?? null)
    setPercentOff('10')
    setNameError('')
    setSeedError('')
    // Reset only when the modal opens; lists refetching mustn't wipe the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preset])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setNameError(NAME_REQUIRED)
    if (nameTaken(lists, trimmed)) {
      return setNameError(`A pricing list named “${trimmed}” already exists.`)
    }
    const source = lists.find((l) => l.id === sourceListId)
    if (startFrom === 'duplicate' && !source) return setSeedError('Pick a list to duplicate.')
    const percent = Number(percentOff)
    if (startFrom === 'percent_off' && !(percent > 0 && percent <= 100)) {
      return setSeedError('Enter a percent between 0 and 100.')
    }

    setSaving(true)
    try {
      await createPricingList(slug, {
        name: trimmed,
        description: description.trim() || null,
        start_from: startFrom,
        source_list_id: startFrom === 'duplicate' ? sourceListId : null,
        percent_off: startFrom === 'percent_off' ? percentOff.trim() : null,
      })
      if (startFrom === 'duplicate' && source) {
        showSuccess(`“${trimmed}” created with ${source.entry_count} entries from ${source.name}`)
      } else if (startFrom === 'percent_off') {
        showSuccess(`“${trimmed}” created at ${percent}% off on all ${productTotal} products`)
      } else {
        showSuccess(`“${trimmed}” created — every product inherits its default price`)
      }
      onClose()
      onDone()
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) {
        setNameError(`A pricing list named “${trimmed}” already exists.`)
      } else {
        showError(errorMessage(err, 'Failed to create pricing list. Please try again.'))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="New pricing list" size="md">
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className="text-sm text-gray-600 -mt-2">
          Any product without an entry inherits its default price.
        </p>

        <FormField label="List name" required htmlFor="pricing-list-name" error={nameError}>
          <Input
            id="pricing-list-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setNameError('')
            }}
            placeholder="e.g. Clinic Tier 2"
            maxLength={100}
            error={!!nameError}
            disabled={saving}
            autoFocus
          />
        </FormField>

        <FormField label="Description" htmlFor="pricing-list-description">
          <Input
            id="pricing-list-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Who this list is for"
            maxLength={500}
            disabled={saving}
          />
        </FormField>

        <div>
          <div className="text-sm font-medium text-charcoal mb-2">Start from</div>
          <div className="space-y-2" role="radiogroup" aria-label="Start from">
            {START_FROM_OPTIONS.map((option) => {
              const active = startFrom === option.key
              const disabled = option.key === 'duplicate' && lists.length === 0
              return (
                <div
                  key={option.key}
                  className={`rounded-md border ${
                    active ? 'border-deep-purple bg-soft-lavender/40' : 'border-gray-200'
                  }`}
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={disabled || saving}
                    onClick={() => {
                      setStartFrom(option.key)
                      setSeedError('')
                    }}
                    className="w-full text-left px-4 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="text-sm font-medium text-charcoal">{option.label}</div>
                    <div className="text-xs text-gray-500">
                      {disabled ? 'No lists to duplicate yet' : option.hint}
                    </div>
                  </button>

                  {active && option.key === 'duplicate' && (
                    <div className="px-4 pb-4">
                      <FormField label="Copy from">
                        <Select
                          options={lists.map((l) => ({
                            value: l.id,
                            label: `${l.name} · ${l.entry_count} ${l.entry_count === 1 ? 'entry' : 'entries'}`,
                          }))}
                          value={sourceListId}
                          onChange={(value) => {
                            setSourceListId(value)
                            setSeedError('')
                          }}
                          disabled={saving}
                        />
                      </FormField>
                    </div>
                  )}

                  {active && option.key === 'percent_off' && (
                    <div className="px-4 pb-4">
                      <FormField
                        label="Percent off the default price"
                        htmlFor="pricing-list-percent"
                        helperText={`Applied to all ${productTotal} products. Stays linked to each default price.`}
                      >
                        <div className="flex items-center gap-2">
                          <Input
                            id="pricing-list-percent"
                            type="number"
                            step="0.5"
                            min="0"
                            max="100"
                            value={percentOff}
                            onChange={(e) => {
                              setPercentOff(e.target.value)
                              setSeedError('')
                            }}
                            className="w-28"
                            disabled={saving}
                          />
                          <span className="text-sm text-gray-600">% off</span>
                        </div>
                      </FormField>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {seedError && <p className="mt-2 text-sm text-error">{seedError}</p>}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving} className="sm:order-1">
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={saving} className="sm:order-2 sm:ml-auto">
            Create list
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/* --------------------------------------------------------------------------
   Edit name + description
   -------------------------------------------------------------------------- */

interface EditPricingListModalProps {
  list: PricingList | null
  slug: string
  lists: PricingList[]
  onClose: () => void
  onDone: () => void
}

export function EditPricingListModal({ list, slug, lists, onClose, onDone }: EditPricingListModalProps) {
  const { success: showSuccess, error: showError } = useToast()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [nameError, setNameError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!list) return
    setName(list.name)
    setDescription(list.description ?? '')
    setNameError('')
  }, [list])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!list) return
    const trimmed = name.trim()
    if (!trimmed) return setNameError(NAME_REQUIRED)
    if (nameTaken(lists, trimmed, list.id)) {
      return setNameError(`A pricing list named “${trimmed}” already exists.`)
    }

    setSaving(true)
    try {
      await updatePricingList(slug, list.id, {
        name: trimmed,
        description: description.trim(),
      })
      showSuccess(`“${trimmed}” saved`)
      onClose()
      onDone()
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) {
        setNameError(`A pricing list named “${trimmed}” already exists.`)
      } else {
        showError(errorMessage(err, 'Failed to save pricing list. Please try again.'))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={list !== null} onClose={onClose} title="Rename pricing list" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600 -mt-2">
          Renaming doesn’t change any prices or assignments.
        </p>
        <FormField label="List name" required htmlFor="edit-pricing-list-name" error={nameError}>
          <Input
            id="edit-pricing-list-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setNameError('')
            }}
            maxLength={100}
            error={!!nameError}
            disabled={saving}
            autoFocus
          />
        </FormField>
        <FormField label="Description" htmlFor="edit-pricing-list-description">
          <Input
            id="edit-pricing-list-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Who this list is for"
            maxLength={500}
            disabled={saving}
          />
        </FormField>
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving} className="sm:order-1">
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={saving} className="sm:order-2 sm:ml-auto">
            Save
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/* --------------------------------------------------------------------------
   Delete, moving providers to another list or the default price
   -------------------------------------------------------------------------- */

interface DeletePricingListModalProps {
  list: PricingList | null
  slug: string
  lists: PricingList[]
  onClose: () => void
  onDone: () => void
}

export function DeletePricingListModal({ list, slug, lists, onClose, onDone }: DeletePricingListModalProps) {
  const { success: showSuccess, error: showError } = useToast()
  const [target, setTarget] = useState(DEFAULT_PRICE_OPTION)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (list) setTarget(DEFAULT_PRICE_OPTION)
  }, [list])

  const targetOptions = [
    { value: DEFAULT_PRICE_OPTION, label: DEFAULT_PRICE_LABEL },
    ...lists.filter((l) => l.id !== list?.id).map((l) => ({ value: l.id, label: l.name })),
  ]

  async function handleDelete() {
    if (!list) return
    const moveTo = target === DEFAULT_PRICE_OPTION ? null : target
    setDeleting(true)
    try {
      await deletePricingList(slug, list.id, moveTo)
      const targetLabel = targetOptions.find((o) => o.value === target)?.label ?? DEFAULT_PRICE_LABEL
      showSuccess(
        `“${list.name}” deleted` +
          (list.provider_count > 0 ? `. Its providers moved to ${targetLabel}.` : '')
      )
      onClose()
      onDone()
    } catch (err) {
      showError(errorMessage(err, 'Failed to delete pricing list. Please try again.'))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Modal open={list !== null} onClose={onClose} title={list ? `Delete “${list.name}”?` : ''} size="sm">
      {list && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 -mt-2">
            {list.entry_count} {list.entry_count === 1 ? 'price' : 'prices'} on this list will be removed.
          </p>

          {list.provider_count > 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 space-y-3">
              <div className="flex gap-2 text-sm text-amber-900">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  {providerCountLabel(list.provider_count)}{' '}
                  {list.provider_count === 1 ? 'is' : 'are'} on this list.
                </span>
              </div>
              <FormField label="Move them to">
                <Select
                  options={targetOptions}
                  value={target}
                  onChange={setTarget}
                  disabled={deleting}
                />
              </FormField>
            </div>
          ) : (
            <p className="text-sm text-gray-600">No providers use this list.</p>
          )}

          <p className="text-sm font-medium text-error">This can’t be undone.</p>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button variant="secondary" onClick={onClose} disabled={deleting} className="sm:order-1">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              loading={deleting}
              className="sm:order-2 sm:ml-auto"
            >
              Delete list
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
